import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlignRight, ChevronDown, User, Package2, LogOut } from 'lucide-react';
import MobileMenu from './MobileMenu';
import SocialLinks from '../SocialLinks';
import RequestForm from '../services/RequestForm';
import { supabase } from '../../lib/supabaseClient';

function NavigationMenu() {
  const [isOpen, setIsOpen] = useState(false);
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [isServicesOpen, setIsServicesOpen] = useState(false);
  const [isPricesOpen, setIsPricesOpen] = useState(false);
  const [showRequestForm, setShowRequestForm] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    return localStorage.getItem('isAuthenticated') === 'true';
  });
  const [showUserMenu, setShowUserMenu] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    // Check authentication status on mount and when auth state changes
    const checkAuth = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      const isAuthenticatedLocal = localStorage.getItem('isAuthenticated') === 'true';
      setIsAuthenticated(!!session || isAuthenticatedLocal);
    };

    checkAuth();

    // Subscribe to auth state changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setIsAuthenticated(!!session || localStorage.getItem('isAuthenticated') === 'true');
    });

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  const handleProfileClick = () => {
    if (isAuthenticated) {
      navigate('/client/dashboard');
    } else {
      navigate('/login');
    }
  };

  const handleLogout = async () => {
    // Clear Supabase session
    await supabase.auth.signOut();
    
    // Clear local storage
    localStorage.removeItem('isAuthenticated');
    localStorage.removeItem('isAdmin');
    localStorage.removeItem('userId');
    localStorage.removeItem('userFirstName');
    localStorage.removeItem('userLastName');
    localStorage.removeItem('userPhone');
    localStorage.removeItem('userEmail');
    localStorage.removeItem('companyName');
    localStorage.removeItem('telegramBotToken');
    localStorage.removeItem('telegramChatId');
    
    // Update authentication state
    setIsAuthenticated(false);
    setShowUserMenu(false);
    
    // Redirect to home page
    navigate('/');
  };

  const handleDropdownMouseEnter = (menu: string) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    
    if (menu !== 'services') setIsServicesOpen(false);
    if (menu !== 'prices') setIsPricesOpen(false);
    if (menu !== 'profile') setIsProfileOpen(false);

    if (menu === 'services') setIsServicesOpen(true);
    if (menu === 'prices') setIsPricesOpen(true);
    if (menu === 'profile') setIsProfileOpen(true);
  };

  const handleDropdownMouseLeave = () => {
    timeoutRef.current = setTimeout(() => {
      setIsServicesOpen(false);
      setIsPricesOpen(false);
      setIsProfileOpen(false);
    }, 150);
  };

  const handleContentMouseEnter = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
  };

  return (
    <>
      <style>
        {`
          .nav-item {
            font-size: 12px;
            transition: color 0.2s ease;
          }
          
          .nav-item:hover {
            background-color: rgba(255, 255, 255, 0.1);
          }

          .dropdown-item {
            font-size: 12px;
            transition: color 0.2s ease;
            padding: 8px 12px;
            border-radius: 6px;
          }

          .dropdown-item:hover {
            background-color: rgba(255, 255, 255, 0.1);
          }
        `}
      </style>

      <nav className="fixed w-full z-50">
        <div className="bg-white relative z-50 shadow-lg">
          <div className="max-w-[980px] mx-auto">
            <div className="flex justify-between items-center h-full">
              {/* Mobile Menu Button - Left */}
              <div className="md:hidden px-3">
                <button
                  onClick={() => setIsOpen(!isOpen)}
                  className="w-10 h-10 flex items-center justify-center text-gray-700 hover:text-primary transition-all rounded-full hover:bg-gray-100 active:scale-95"
                >
                  <AlignRight className="h-5 w-5" />
                </button>
              </div>

              {/* Logo - Left on desktop, center on mobile */}
              <div className="hidden md:block px-3">
                <a href="/" className="flex items-center">
                  <img
                    src="https://i.ibb.co/sJQNRg3S/image.png"
                    alt="997 Cargo"
                    className="h-[72px]"
                  />
                </a>
              </div>
              <div className="md:hidden absolute left-1/2 transform -translate-x-1/2">
                <a href="/" className="flex items-center">
                  <img 
                    src="https://i.ibb.co/sJQNRg3S/image.png"
                    alt="997 Cargo"
                    className="h-[54px]"
                  />
                </a>
              </div>

              {/* Desktop Navigation - Hidden on Mobile */}
              <div className="hidden md:flex items-center justify-center flex-1">
                <div 
                  className="relative"
                  onMouseEnter={() => handleDropdownMouseEnter('services')}
                  onMouseLeave={handleDropdownMouseLeave}
                >
                  <button className="nav-item text-gray-700 font-medium flex items-center px-3 py-2 rounded-full hover:bg-gray-100 transition-all">
                    Услуги 
                    <ChevronDown className={`ml-1 h-3 w-3 transform transition-transform duration-300 ${isServicesOpen ? 'rotate-180' : ''}`} />
                  </button>
                </div>

                <a href="/prices" className="nav-item text-gray-700 font-medium px-3 py-2 rounded-full hover:bg-gray-100 transition-all">
                  Цены
                </a>

                <button 
                  onClick={() => setShowRequestForm(true)}
                  className="nav-item text-white font-medium px-4 py-2 rounded-full bg-[#1B3BA0] hover:bg-white hover:text-[#1B3BA0] border border-[#1B3BA0] transition-all flex items-center hover:shadow-md active:scale-95"
                >
                  <Package2 className="w-3 h-3 mr-1" />
                  Заказать доставку
                </button>
                <a href="/about" className="nav-item text-gray-700 font-medium px-3 py-2 rounded-full hover:bg-gray-100 transition-all">
                  О нас
                </a>
                <a href="/contacts" className="nav-item text-gray-700 font-medium px-3 py-2 rounded-full hover:bg-gray-100 transition-all">
                  Контакты
                </a>
              </div>

              {/* User Icon - Right */}
              <div className="px-3 relative">
                <button
                  onClick={() => isAuthenticated ? setShowUserMenu(!showUserMenu) : handleProfileClick()}
                  className="w-10 h-10 flex items-center justify-center text-gray-700 hover:text-primary transition-all rounded-full hover:bg-gray-100 active:scale-95"
                  title={isAuthenticated ? 'Личный кабинет' : 'Войти'}
                >
                  <User className="h-5 w-5" />
                </button>
                
                {/* User Menu Dropdown */}
                {isAuthenticated && showUserMenu && (
                  <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg py-1 z-50">
                    <a
                      href="/client/dashboard"
                      className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                    >
                      Личный кабинет
                    </a>
                    <button
                      onClick={handleLogout}
                      className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-gray-100 flex items-center"
                    >
                      <LogOut className="w-4 h-4 mr-2" />
                      Выйти
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Dropdown Menus */}
        <div 
          className={`absolute w-full bg-white shadow-lg transition-all duration-300 ease-out ${
            isServicesOpen || isPricesOpen
              ? 'opacity-100 translate-y-0 visible'
              : 'opacity-0 -translate-y-1 invisible pointer-events-none'
          }`}
          onMouseEnter={handleContentMouseEnter}
          onMouseLeave={handleDropdownMouseLeave}
        >
          {isServicesOpen ? (
            <div className="max-w-[980px] mx-auto py-8 px-3">
              <div className="space-y-1">
                <a href="/services/business/search" className="dropdown-item block text-gray-700 hover:bg-gray-100 hover:text-[#1B3BA0]">
                  Поиск и выкуп товаров
                </a>
                <a href="/services/business/logistics" className="dropdown-item block text-gray-700 hover:bg-gray-100 hover:text-[#1B3BA0]">
                  Логистика и таможня
                </a>
                <a href="/services/business/forwarding" className="dropdown-item block text-gray-700 hover:bg-gray-100 hover:text-[#1B3BA0]">
                  Инспекционный контроль
                </a>
                <a href="/services/business/payment" className="dropdown-item block text-gray-700 hover:bg-gray-100 hover:text-[#1B3BA0]">
                  Оплата поставщикам
                </a>
              </div>
            </div> 
          ) : null}
        </div>
      </nav>

      <MobileMenu 
        isOpen={isOpen} 
        onClose={() => setIsOpen(false)}
        isAuthenticated={isAuthenticated} 
        onLogout={() => {}}
      />

      {/* Request Form Popup */}
      {showRequestForm && (
        <RequestForm 
          isPopup 
          source="Главное меню"
          onClose={() => setShowRequestForm(false)} 
        />
      )}
    </>
  );
}

export default NavigationMenu;